import { useRef, ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { rowMeasure } from './rowMeasure'

interface VirtualizedTokenListProps<T> {
  tokens: T[]
  renderToken: (token: T, index: number) => ReactNode
  estimateSize?: number | ((index: number) => number)
  getItemKey?: (index: number) => string | number
  overscan?: number
  height?: string | number
}

export function VirtualizedTokenList<T>({
  tokens,
  renderToken,
  estimateSize = 160,
  getItemKey,
  overscan = 5,
  height = '400px',
}: VirtualizedTokenListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null)
  const sizeAt = (index: number) => (typeof estimateSize === 'function' ? estimateSize(index) : estimateSize)

  const virtualizer = useVirtualizer({
    count: tokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: sizeAt,
    ...(getItemKey ? { getItemKey } : {}),
    overscan,
    measureElement: (element, entry, instance) => rowMeasure(element, entry, instance, sizeAt),
  })

  return (
    <div
      ref={parentRef}
      className="overflow-auto"
      style={{ height }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const token = tokens[virtualRow.index]
          if (!token) return null
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {renderToken(token, virtualRow.index)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
